/**
 * CheckupReusePrompt (BL-214)
 *
 * The "reuse or take a new one" screen shown inside a CheckUp widget when a
 * valid banked result already exists (see `useCheckupReuse`). Presentation only
 * — the parent widget owns the two outcomes:
 *   • onReuse      → complete the step with the existing result (no re-capture)
 *   • onRecordNew  → fall through to the widget's normal capture flow
 *
 * E2E anchors: this prompt renders INSTEAD of the widget root
 * (`widget-pitch_recorder` / `widget-language_test` / `widget-motivational_form`),
 * so without a testid of its own a driver simply times out waiting for a root
 * that will never mount. `checkup-reuse-new` is the escape hatch block-03 uses
 * to reach the normal capture flow on a re-run.
 */

import { useLocale, useTranslation } from '@tonnie/locale';
import { Button } from '@tonnie/ui';
import { CheckCircle2, RotateCcw, Video } from '@tonnie/assets';
import type { TalentTestResultView } from '@tonnie/sdk';

interface CheckupReusePromptProps {
  result: TalentTestResultView;
  onReuse: () => void;
  onRecordNew: () => void;
}

export function CheckupReusePrompt({ result, onReuse, onRecordNew }: CheckupReusePromptProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  const takenOn = formatDate(result.takenAt, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      data-testid="checkup-reuse-prompt"
      // `data-test-unit` says WHICH reusable unit is being offered, so a driver
      // can assert it landed on the pitch prompt rather than the language one.
      data-test-unit={result.testId}
      className="flex flex-col items-center justify-center gap-6 p-8 text-center"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-success/10 text-success-accessible">
        <CheckCircle2 className="h-7 w-7" />
      </div>

      <div className="space-y-1.5">
        <h3 className="text-lg font-semibold text-foreground">{t('checkup.reuse.title')}</h3>
        <p className="max-w-sm text-sm text-muted-foreground">{t('checkup.reuse.subtitle')}</p>
      </div>

      {/* The banked result card */}
      <div className="w-full max-w-sm rounded-xl border border-border/70 bg-muted/40 p-4 text-left">
        <p data-testid="checkup-reuse-label" className="font-medium text-foreground">
          {t(result.labelKey)}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <span>{t('checkup.reuse.takenOn', { date: takenOn })}</span>
          {result.score ? (
            <>
              <span aria-hidden>·</span>
              <span className="font-semibold text-foreground">{result.score}</span>
            </>
          ) : null}
          {result.daysRemaining != null ? (
            <>
              <span aria-hidden>·</span>
              <span className="text-tone-green">
                {t('checkup.reuse.daysLeft', { count: result.daysRemaining })}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex w-full max-w-sm flex-col gap-2.5 sm:flex-row">
        <Button data-testid="checkup-reuse-accept" className="flex-1 gap-2" onClick={onReuse}>
          <RotateCcw className="h-4 w-4" />
          {t('checkup.reuse.reuseCta')}
        </Button>
        <Button
          data-testid="checkup-reuse-new"
          variant="outline"
          className="flex-1 gap-2"
          onClick={onRecordNew}
        >
          <Video className="h-4 w-4" />
          {t('checkup.reuse.newCta')}
        </Button>
      </div>
    </div>
  );
}
